import { useCallback, useEffect, useRef, useState } from 'react';

/** Reads/toggles ?projector=1 in the URL (so a kiosk can autostart into it). */
export function useProjectorMode(): { projector: boolean; toggleProjector: () => void } {
  const [projector, setProjector] = useState(
    () => new URLSearchParams(location.search).get('projector') === '1',
  );

  const toggleProjector = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const next = params.get('projector') !== '1';
    if (next) params.set('projector', '1');
    else params.delete('projector');
    history.replaceState(null, '', `${location.pathname}?${params.toString()}`);
    setProjector(next);
  }, []);

  return { projector, toggleProjector };
}

interface Props {
  areaName: string | null;
  onExit: () => void;
}

/**
 * Ambient overlay for projector mode: requests fullscreen, shows a corner
 * clock + area name, auto-hides the mouse cursor after 3 s idle, and exits
 * back to normal mode on any key press.
 */
export default function ProjectorOverlay({ areaName, onExit }: Props) {
  const [time, setTime] = useState(() => new Date());
  const cursorTimer = useRef<number | undefined>(undefined);

  // Clock tick.
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Best-effort fullscreen on entry (browsers may require a user gesture).
  useEffect(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => undefined);
    }
    return () => {
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined);
    };
  }, []);

  // Cursor auto-hide after 3 s idle; any key exits projector mode.
  useEffect(() => {
    const hide = () => document.body.classList.add('cursor-hidden');
    const onMove = () => {
      document.body.classList.remove('cursor-hidden');
      window.clearTimeout(cursorTimer.current);
      cursorTimer.current = window.setTimeout(hide, 3000);
    };
    const onKey = () => onExit();
    const onClick = () => onExit();
    onMove();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('keydown', onKey);
    window.addEventListener('click', onClick);
    return () => {
      window.clearTimeout(cursorTimer.current);
      document.body.classList.remove('cursor-hidden');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('click', onClick);
    };
  }, [onExit]);

  return (
    <div className="projector-overlay">
      <div className="projector-corner">
        <span className="projector-clock">
          {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
        {areaName && <span className="projector-area">{areaName}</span>}
      </div>
    </div>
  );
}
